import { useState, KeyboardEvent } from "react";
import { Collapse, IconButton } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as ChevronRightIcon } from "../../../assets/chevron-right.icon.svg";
import { ReactComponent as ChevronDownIcon } from "../../../assets/chevron-down.icon.svg";

// Enter/Space activation for the expandable rows, which are role="button"
// <div>s rather than native buttons (WCAG 2.1.1).
const clickOnKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
    }
};

const isExpandable = (value: any): boolean => {
    return value !== null && typeof value === "object";
};

const formatValue = (value: any): { text: string; className: string } => {
    if (value === null) return { text: "null", className: "metadata-value--null" };
    if (value === undefined) return { text: "undefined", className: "metadata-value--null" };
    if (typeof value === "boolean")
        return { text: String(value), className: "metadata-value--boolean" };
    if (typeof value === "number")
        return { text: String(value), className: "metadata-value--number" };
    if (typeof value === "string")
        return { text: `"${value}"`, className: "metadata-value--string" };
    return { text: String(value), className: "" };
};

const getPreview = (value: any): string => {
    if (Array.isArray(value)) return `Array(${value.length})`;
    const keys = Object.keys(value);
    return `{${keys.length}}`;
};

const MetadataTreeNode = ({
    nodeKey,
    value,
    level
}: {
    nodeKey: string;
    value: any;
    level: number;
}) => {
    const [expanded, setExpanded] = useState(false);
    const expandable = isExpandable(value);

    if (!expandable) {
        const formatted = formatValue(value);
        return (
            <div className="metadata-tree-row" style={{ paddingLeft: level * 16 }}>
                <span className="metadata-tree-row__spacer" />
                <span className="metadata-key">{nodeKey}</span>
                <span className="metadata-separator">: </span>
                <span className={`metadata-value ${formatted.className}`}>{formatted.text}</span>
            </div>
        );
    }

    const entries = Array.isArray(value)
        ? value.map((v, i) => [String(i), v] as [string, any])
        : Object.entries(value);

    return (
        <div className="metadata-tree-node">
            <div
                className="metadata-tree-row metadata-tree-row--expandable"
                style={{ paddingLeft: level * 16 }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={clickOnKey}
            >
                <IconButton
                    className="metadata-tree-row__toggle"
                    size="small"
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    <SvgIcon
                        component={expanded ? ChevronDownIcon : ChevronRightIcon}
                        inheritViewBox
                    />
                </IconButton>
                <span className="metadata-key">{nodeKey}</span>
                <span className="metadata-separator">: </span>
                <span className="metadata-preview">{getPreview(value)}</span>
            </div>
            <Collapse in={expanded} unmountOnExit>
                {entries.map(([childKey, childValue]) => (
                    <MetadataTreeNode
                        key={childKey}
                        nodeKey={childKey}
                        value={childValue}
                        level={level + 1}
                    />
                ))}
            </Collapse>
        </div>
    );
};

const MetadataTree = ({ data }: { data: Record<string, any> }) => {
    const entries = Object.entries(data);

    if (entries.length === 0) {
        return <div className="metadata-tree metadata-tree--empty">No metadata</div>;
    }

    return (
        <div className="metadata-tree">
            {entries.map(([key, value]) => (
                <MetadataTreeNode key={key} nodeKey={key} value={value} level={0} />
            ))}
        </div>
    );
};

export default MetadataTree;
